import React, { useState, useEffect, useRef, useCallback } from "react";
import { resolveCameraTimezone } from "../../utils/cameraTimezone";
import { useNavigate } from "react-router-dom";
import "./EquipmentPage.css";

import { listFactories } from "../../services/factories";
import { listWorkshops } from "../../services/workshops";
import { listSections } from "../../services/sections";
import { listRooms } from "../../services/rooms";
import { getLastFrame, listCameras, getCamera } from "../../services/cameras";
import { isAdmin } from "../../services/api";
import { formatCameraDate } from "../../utils/formatCameraDate";
import { exportRoomFramesXlsx, getFrame, listFrames } from "../../services/frames";
import FrameGantt from "../../components/FrameGantt/FrameGantt";
import { EQUIPMENT_GANTT_METRICS, disabledMetricsFromConfig } from "../../components/FrameGantt/frameGanttModel";
import { getVideoSettings, stopLive, warmupLive } from "../../services/liveArchive";
import { useNavigationState } from "../../hooks/useNavigationState";
import { useShortestHierarchy } from "../../hooks/useShortestHierarchy";
import { isCameraVisibleForPage } from "../../utils/shortestHierarchy";

import { Magnifier, ChevronLeft } from "@gravity-ui/icons";
import EquipmentTable from "./EquipmentTable";
import ZoomModal from "../../components/ZoomModal/ZoomModal";
import CameraCard from "../../components/CameraCard/CameraCard";
import EntityImage from "../../components/EntityImage/EntityImage";
import { DetectionZonesLegend, DetectionZonesOverlay } from "../../components/DetectionZonesOverlay/DetectionZonesOverlay";
import useCameraZones from "../../hooks/useCameraZones";
import { CameraAdminButton, CameraVideoButtons, LocationAdminButton } from "../../components/CameraPageLinks/CameraPageLinks";
import { openCameraInAdmin } from "../../utils/camerasDrilldown";

const levelMap = {
  factories: "factory",
  workshops: "workshop",
  sections: "section",
  rooms: "room",
  cameras: "camera",
  frames: "frame",
};

const EMPTY_FILTERS = {
  start_date: "",
  end_date: "",
  signal_type: "",
  sortBy: "",
  sortOrder: "",
};

/**
 * Извлекаем смены из объекта камеры.
 * Если shift_start у смены равен null — смена не существует и не включается.
 */
const extractShiftsFromCamera = (camera) => {
  if (!camera) return [];
  const shifts = [];
  for (let i = 1; i <= 3; i++) {
    const start = camera[`shift${i}_start`];
    const end   = camera[`shift${i}_end`];
    if (start && end) {
      shifts.push({ start: start.slice(0, 5), end: end.slice(0, 5) });
    }
  }
  return shifts;
};

const extractLastFrame = (result) => {
  if (!result) return null;
  if (Array.isArray(result)) return result[0] ?? null;
  if (result && typeof result === "object" && "items" in result) return result.items?.[0] ?? null;
  return null;
};

// ─── Основной компонент ────────────────────────────────────────────────────────
const EquipmentPage = ({ cameraType = "EQUIPMENT" }) => {
  const navigate = useNavigate();
  const { currentLevel, setCurrentLevel, selectedItems, setSelectedItems } =
    useNavigationState("equipmentPage_nav");

  // ТЗ №24: кратчайшая иерархия — пустые ветви скрыты, единственные уровни пропущены.
  // Без дерева (загрузка не удалась) — полная навигация по уровням, как раньше.
  const hierarchy = useShortestHierarchy(cameraType, {
    currentLevel, selectedItems, setCurrentLevel, setSelectedItems,
  });
  const { locationData, locationPending } = hierarchy;

  const [currentData,    setCurrentData]    = useState([]);
  const [selectedFrame,  setSelectedFrame]  = useState(null);
  // ТЗ №2 §10.3: root display_timezone списка кадров (приоритет над CameraResponse)
  const [framesTimezone, setFramesTimezone] = useState("");
  const [loading,        setLoading]        = useState(false);
  const [error,          setError]          = useState("");
  const [lastFramesMap,  setLastFramesMap]  = useState(new Map());
  const mountedRef = useRef(true);

  // Пагинация
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize,    setPageSize]    = useState(50);
  const [totalFrames, setTotalFrames] = useState(0);
  const totalPages = Math.max(1, Math.ceil(totalFrames / pageSize));

  // Серверные фильтры
  const [filters, setFilters] = useState({ ...EMPTY_FILTERS });

  // Клиентский фильтр смен — lifted сюда, чтобы сброс работал корректно
  // Полные данные камеры (включая смены)
  const [cameraDetails, setCameraDetails] = useState(null);
  const [videoSettings, setVideoSettings] = useState(null);
  const [cameraNotFound, setCameraNotFound] = useState(false);

  // Зум
  const [zoomSrc, setZoomSrc] = useState(null);
  // ТЗ V5 №6: зоны камеры открытого кадра; слой можно скрыть, по умолчанию виден.
  const frameZones = useCameraZones(currentLevel === "frames" ? selectedItems.camera?.id ?? null : null);
  const [zonesVisible, setZonesVisible] = useState(true);

  const cacheRef = useRef({
    factories: null,
    workshops: new Map(),
    sections:  new Map(),
    rooms:     new Map(),
    cameras:   new Map(),
  });
  const previewCacheRef = useRef(new Map());
  const previewRequestRef = useRef(new Set());
  const liveOpenRequestedRef = useRef(false);

  useEffect(() => {
    cacheRef.current.cameras.clear();
  }, [cameraType]);

  // ─── Параметры запроса ───────────────────────────────────────────────────────
  const buildQueryParams = useCallback(
    (page = currentPage, size = pageSize) => {
      const params = { limit: size, offset: (page - 1) * size };
      for (const [key, value] of Object.entries(filters)) {
        if (value !== "" && value !== null && value !== undefined) {
          params[key] = value;
        }
      }
      return params;
    },
    [filters, currentPage, pageSize]
  );

  // Превью карточки — свежий последний кадр; до его загрузки CameraCard
  // показывает original из самой камеры (он уже в списке).
  const loadLastFramesForCameras = useCallback(async (cameras) => {
    const missing = cameras.filter((camera) => (
      !previewCacheRef.current.has(camera.id) &&
      !previewRequestRef.current.has(camera.id)
    ));

    if (!missing.length) return;

    missing.forEach((camera) => previewRequestRef.current.add(camera.id));

    const results = await Promise.all(
      missing.map(async (camera) => {
        try {
          const result = await getLastFrame(camera.id);
          return { id: camera.id, frame: extractLastFrame(result) };
        } catch {
          return { id: camera.id, frame: null };
        }
      })
    );

    if (!mountedRef.current) return;

    setLastFramesMap((prev) => {
      const next = new Map(prev);
      results.forEach(({ id, frame }) => {
        previewCacheRef.current.set(id, frame);
        previewRequestRef.current.delete(id);
        next.set(id, frame);
      });
      return next;
    });
  }, []);

  // ─── Загрузка данных ─────────────────────────────────────────────────────────
  const loadData = useCallback(async () => {
    if (!mountedRef.current) return;
    setLoading(true);
    setError("");
    if (locationPending) return;
    if (locationData) {
      setCurrentData(locationData);
      setLoading(false);
      return;
    }
    try {
      if (currentLevel === "factories") {
        let data = cacheRef.current.factories;
        if (!data) { data = await listFactories(); cacheRef.current.factories = data; }
        if (mountedRef.current) setCurrentData(data);
      }
      if (currentLevel === "workshops" && selectedItems.factory) {
        const id = selectedItems.factory.id;
        let data = cacheRef.current.workshops.get(id);
        if (!data) { data = await listWorkshops(id); cacheRef.current.workshops.set(id, data); }
        if (mountedRef.current) setCurrentData(data);
      }
      if (currentLevel === "sections" && selectedItems.workshop) {
        const id = selectedItems.workshop.id;
        let data = cacheRef.current.sections.get(id);
        if (!data) { data = await listSections(id); cacheRef.current.sections.set(id, data); }
        if (mountedRef.current) setCurrentData(data);
      }
      if (currentLevel === "rooms" && selectedItems.section) {
        const id = selectedItems.section.id;
        let data = cacheRef.current.rooms.get(id);
        if (!data) { data = await listRooms(id); cacheRef.current.rooms.set(id, data); }
        if (mountedRef.current) setCurrentData(data);
      }
      if (currentLevel === "cameras" && selectedItems.room) {
        const roomId   = selectedItems.room.id;
        const cacheKey = `${roomId}_${cameraType}`;
        let data = cacheRef.current.cameras.get(cacheKey);
        if (!data) {
          data = await listCameras(roomId, { camera_type: cameraType });
          cacheRef.current.cameras.set(cacheKey, data);
        }
        if (mountedRef.current) {
          setCurrentData(data);
          const cachedFrames = data
            .filter((camera) => previewCacheRef.current.has(camera.id))
            .map((camera) => [camera.id, previewCacheRef.current.get(camera.id)]);
          if (cachedFrames.length > 0) {
            setLastFramesMap((prev) => new Map([...prev, ...cachedFrames]));
          }
          loadLastFramesForCameras(data);
        }
      }
      if (currentLevel === "frames" && selectedItems.camera) {
        const params = buildQueryParams(currentPage, pageSize);
        const response = await listFrames(selectedItems.camera.id, params);
        const framesArray = response.items || [];
        const total = response.total ?? response.count ?? framesArray.length;
        if (mountedRef.current) {
          setCurrentData(framesArray);
          setFramesTimezone(response.display_timezone || "");
          setTotalFrames(total);
          setSelectedFrame(prev =>
            prev && framesArray.some(f => f.id === prev.id) ? prev : framesArray[0] ?? null
          );
        }
      }
    } catch (e) {
      console.error("Ошибка загрузки данных", e);
      if (mountedRef.current) {
        setError("Не удалось загрузить данные. Попробуйте обновить страницу.");
      }
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [
    currentLevel, locationData, locationPending,
    selectedItems.factory, selectedItems.workshop,
    selectedItems.section, selectedItems.room, selectedItems.camera,
    buildQueryParams, cameraType, currentPage, pageSize, loadLastFramesForCameras,
  ]);

  // ─── Применение фильтра ──────────────────────────────────────────────────────
  const handleApplyFilter = useCallback((values) => {
    setFilters(prev => {
      const next = { ...prev, ...values };
      if (JSON.stringify(prev) === JSON.stringify(next)) return prev;
      return next;
    });
    setCurrentPage(1);
  }, []);

  // Проверяем наличие активных фильтров, включая клиентский фильтр смен
  const hasActiveFilters = Object.values(filters).some((value) =>
    Array.isArray(value) ? value.length > 0 : value !== "" && value !== null && value !== undefined
  );

  const handleResetAllFilters = () => {
    setFilters({ ...EMPTY_FILTERS });
    setCurrentPage(1);
  };

  // Сброс при смене камеры
  useEffect(() => {
    let cancelled = false;
    let warmedUp = false;
    const cameraId = selectedItems.camera?.id;

    setFilters({ ...EMPTY_FILTERS });
    setCurrentPage(1);
    setTotalFrames(0);
    setCameraDetails(null);
    setVideoSettings(null);
    setCameraNotFound(false);
    liveOpenRequestedRef.current = false;

    if (cameraId) {
      getCamera(cameraId)
        .then(data => setCameraDetails(data))
        .catch(e => {
          if (cancelled) return;
          console.error("Ошибка загрузки камеры:", e);
          if (e?.response?.status === 404) {
            setCameraNotFound(true);
            const roomId = selectedItems.room?.id;
            if (roomId) cacheRef.current.cameras.delete(`${roomId}_${cameraType}`);
          }
        });
      getVideoSettings(cameraId)
        .then((data) => {
          if (cancelled) return;
          setVideoSettings(data);
          if (data?.live_enabled && !data?.archive_enabled) {
            warmedUp = true;
            warmupLive(cameraId).catch(() => null);
          }
        })
        .catch(() => setVideoSettings(null));
    }

    return () => {
      cancelled = true;
      if (warmedUp && !liveOpenRequestedRef.current) {
        stopLive(cameraId).catch(() => null);
      }
    };
    // cameraType/selectedItems.room are read inside the getCamera(...).catch handler above,
    // but intentionally excluded here — this effect should only reset/reload on camera change,
    // not re-run the live-warmup/stopLive cleanup logic when the room or cameraType changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItems.camera]);

  useEffect(() => {
    if (!cameraNotFound) return;
    const timer = setTimeout(() => {
      setSelectedItems((prev) => ({ ...prev, camera: null }));
      setCurrentLevel("cameras");
    }, 1800);
    return () => clearTimeout(timer);
  }, [cameraNotFound, setSelectedItems, setCurrentLevel]);

  useEffect(() => {
    mountedRef.current = true;
    loadData();
    return () => { mountedRef.current = false; };
  }, [loadData]);

  // Автообновление УДАЛЕНО по требованию задачи

  // ─── Навигация ────────────────────────────────────────────────────────────────
  // ТЗ V5 №16: точка диаграммы — точный frame_id. Кадра нет на текущей
  // странице таблицы — читаем его отдельно, указатель двигается сразу.
  const ganttFrameRequestRef = useRef(0);
  const handleGanttSelect = useCallback((frameId) => {
    const request = ++ganttFrameRequestRef.current;
    const local = currentData.find((frame) => frame?.id === frameId && frame?.created_at);
    if (local) {
      setSelectedFrame(local);
      return;
    }
    setSelectedFrame({ id: frameId });
    getFrame(frameId)
      .then((frame) => {
        if (mountedRef.current && request === ganttFrameRequestRef.current) setSelectedFrame(frame);
      })
      .catch(() => {
        if (mountedRef.current && request === ganttFrameRequestRef.current) {
          setSelectedFrame(null);
          setError("Не удалось открыть кадр");
        }
      });
  }, [currentData]);

  const handleItemClick = (item, level) => {
    if (level === "frame") {
      setSelectedFrame(item);
      return;
    }
    setSelectedFrame(null);
    hierarchy.openItem(item, level);
  };

  const handleBreadcrumbClick = (level) => {
    setSelectedFrame(null);
    hierarchy.goToLevel(level);
  };

  // «Назад» — на предыдущий показанный уровень, пропущенные не открываются.
  const handleBack = () => {
    setSelectedFrame(null);
    hierarchy.goBack();
  };

  // ─── Excel ────────────────────────────────────────────────────────────────────
  const handleDownloadExcel = async () => {
    if (!selectedItems.room) return;
    try {
      const exportParams = { ...buildQueryParams(1, pageSize), limit: 10000, offset: 0 };
      const response = await exportRoomFramesXlsx(selectedItems.camera.id, exportParams);
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url  = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href  = url;
      link.download = `room_${selectedItems.room?.id}_export.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error("Ошибка экспорта XLSX", e);
      alert("Не удалось скачать файл");
    }
  };

  const handleOpenLive = () => {
    if (!selectedItems.camera?.id || !canOpenLive) return;
    liveOpenRequestedRef.current = true;
    navigate(`/equipment/${selectedItems.camera.id}/live`);
  };

  const handleOpenArchive = () => {
    if (!selectedItems.camera?.id || !canOpenArchive) return;
    navigate(`/equipment/${selectedItems.camera.id}/archive`);
  };

  const cameraStatus = (cameraDetails?.status || selectedItems.camera?.status || "").toUpperCase();
  const isCameraRunning = cameraStatus === "RUNNING";
  const canOpenLive = Boolean(videoSettings?.live_enabled) && isCameraRunning;
  // Архив доступен пользователю независимо от archive_enabled: выключение архива
  // означает остановку дальнейшей записи, а не запрет доступа к уже накопленным
  // записям. Кнопка активна при наличии доступа к камере (камера выбрана и доступна).
  const canOpenArchive = Boolean(selectedItems.camera?.id) && !cameraNotFound;
  const liveTitle = !videoSettings?.live_enabled
    ? "Для активации прямого эфира обратитесь к администратору"
    : !isCameraRunning
      ? "Камера остановлена"
      : "Прямой эфир";

  // ─── Хлебные крошки ──────────────────────────────────────────────────────────
  const renderBreadcrumbs = () => {
    // Крошка пропущенного уровня — просто подпись, без перехода.
    return (
      <div className="ep-breadcrumbs">
        {hierarchy.crumbs.map((crumb, index) => (
          <React.Fragment key={crumb.key}>
            {index > 0 && " / "}
            {crumb.target
              ? <span onClick={() => handleBreadcrumbClick(crumb.target)}>{crumb.label}</span>
              : <span className="ep-breadcrumb-static">{crumb.label}</span>}
          </React.Fragment>
        ))}
      </div>
    );
  };

  // ─── Рендер контента ──────────────────────────────────────────────────────────
  const renderContent = () => {
    if (loading && currentLevel !== "frames") return <div className="ep-items-grid">Загрузка…</div>;

    if (currentLevel === "frames") {
      if (cameraNotFound) {
        return <div className="ep-frame-placeholder">Камера недоступна</div>;
      }
      const previewSrc = selectedFrame?.processed?.url || selectedFrame?.original?.url;
      return (
        <div className="ep-frames-layout">
          {isAdmin() && cameraDetails?.status === "PENDING_DELETE" && (
            <div className="ep-pending-delete-banner">
              <div>Камера назначена на удаление</div>
              <div>Удаление запрошено {formatCameraDate(cameraDetails.delete_requested_at)}</div>
              <div>Восстановить можно до {formatCameraDate(cameraDetails.delete_scheduled_at)}</div>
              <div>История и кадры доступны администратору до конца срока хранения.</div>
            </div>
          )}
          <div className="ep-toolbar">
            <CameraVideoButtons
              className="ep-download-btn"
              canOpenLive={canOpenLive}
              liveTitle={liveTitle}
              onOpenLive={handleOpenLive}
              canOpenArchive={canOpenArchive}
              onOpenArchive={handleOpenArchive}
            />
            <button className="ep-download-btn" onClick={handleDownloadExcel}>
              Скачать таблицу
            </button>
            <CameraAdminButton className="ep-download-btn" path={selectedItems} />
            {hasActiveFilters && (
              <button className="ep-reset-btn" onClick={handleResetAllFilters}>
                Сбросить фильтры
              </button>
            )}
          </div>

          {/* Превью кадра */}
          <div className="ep-frame-preview">
            {selectedFrame && previewSrc ? (
              <>
                <DetectionZonesOverlay
                  src={previewSrc}
                  alt="Кадр"
                  zones={frameZones.zones}
                  visible={zonesVisible}
                  referenceSrc={selectedFrame?.processed?.url ? selectedFrame?.original?.url || "" : ""}
                  onImageClick={() => previewSrc && setZoomSrc(previewSrc)}
                  imgTitle="Нажмите для увеличения"
                />
                <button
                  className="ep-frame-zoom-btn"
                  onClick={() => previewSrc && setZoomSrc(previewSrc)}
                  title="Увеличить">
                  <Magnifier width={16} height={16} />
                </button>
              </>
            ) : (
              <div className="ep-frame-placeholder">{selectedFrame ? "Загрузка…" : "Выберите кадр"}</div>
            )}
          </div>
          {selectedFrame && (
            <div className="ep-frame-zones">
              <DetectionZonesLegend
                status={frameZones.status}
                zones={frameZones.zones}
                visible={zonesVisible}
                onToggle={setZonesVisible}
                historical
              />
            </div>
          )}

          <div className="ep-frames-list">
            <EquipmentTable
              frames={currentData}
              selectedFrame={selectedFrame}
              onSelectFrame={(frame) => handleItemClick(frame, "frame")}
              isInitialLoading={loading}
              filters={filters}
              onApplyFilter={handleApplyFilter}
              shifts={extractShiftsFromCamera(cameraDetails)}
              timezone={resolveCameraTimezone({ response: { display_timezone: framesTimezone }, camera: cameraDetails })}
              currentPage={currentPage}
              pageSize={pageSize}
              totalPages={totalPages}
              totalItems={totalFrames}
              onPageChange={(p) => setCurrentPage(p)}
              onPageSizeChange={(s) => { setPageSize(s); setCurrentPage(1); }}
              gantt={
                <FrameGantt
                  key={selectedItems.camera?.id}
                  cameraId={selectedItems.camera?.id ?? null}
                  filters={filters}
                  metricOptions={EQUIPMENT_GANTT_METRICS}
                  timezone={resolveCameraTimezone({ response: { display_timezone: framesTimezone }, camera: cameraDetails })}
                  selectedFrameId={selectedFrame?.id ?? null}
                  onSelectFrame={handleGanttSelect}
                  storageKey={"ep_frameGantt"}
                  disabledMetrics={disabledMetricsFromConfig(cameraDetails?.detection_configs)}
                />
              }
            />
          </div>
        </div>
      );
    }

    if (currentLevel === "cameras") {
      return (
        <div className="ep-items-grid">
          {currentData.map((camera) => {
            if (!isCameraVisibleForPage(camera, cameraType, isAdmin())) return null;
            return (
              <CameraCard
                key={camera.id}
                camera={camera}
                lastFrame={lastFramesMap.get(camera.id)}
                onOpen={(c) => handleItemClick(c, "camera")}
                onOpenAdmin={isAdmin() ? (c) => openCameraInAdmin(navigate, c, selectedItems) : undefined}
              />
            );
          })}
        </div>
      );
    }

    if (hierarchy.tree && currentData.length === 0) {
      return <div className="ep-frame-placeholder">Нет доступных камер</div>;
    }

    return (
      <div className="ep-items-grid">
        {currentData.map((item) => (
          <div key={item.id} className="ep-item-card" onClick={() => handleItemClick(item, levelMap[currentLevel])}>
            <div className="ep-item-image">
              <EntityImage
                entityType={levelMap[currentLevel]}
                entityId={item.id}
                image={item.image}
                alt={item.name}
                placeholderClassName="ep-item-image-placeholder"
              />
            </div>
            <div className="ep-item-name">{item.name}</div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="ep-page">
      <div className="ep-header">
        <button className="ep-back-btn" onClick={handleBack} disabled={!hierarchy.backTarget}>
          <ChevronLeft className="g-icon-lead" width={16} height={16} />Назад
        </button>
        <h1>Просмотр кадров (Оборудование)</h1>
        {currentLevel === "cameras" && (
          <div className="ep-toolbar">
            <LocationAdminButton className="ep-download-btn" path={selectedItems} />
          </div>
        )}
      </div>

      {renderBreadcrumbs()}

      {error && <div className="ep-frame-placeholder">{error}</div>}

      <div className="ep-content">{renderContent()}</div>

      {/* Зум-модал */}
      {zoomSrc && (
        <ZoomModal
          src={zoomSrc}
          onClose={() => setZoomSrc(null)}
          zones={zonesVisible ? frameZones.zones : []}
          referenceSrc={selectedFrame?.processed?.url ? selectedFrame?.original?.url || "" : ""}
        />
      )}
    </div>
  );
};

export default EquipmentPage;
